import React from 'react';
import { Link } from 'react-router-dom';

// Display-only page.
// Registration and server-side acceptance must be implemented separately.
// Update the date and version when publishing a later revision.
export const TERMS_VERSION = '2026-10-12-r4';

const LAST_UPDATED = 'October 12, 2026';
const SUPPORT_EMAIL = 'support@hagglefreight.com';

const TERMS_TEXT = `
These Terms of Service ("Terms") are an agreement between you and GoCourier Inc., the operator of HaggleFreight ("HaggleFreight," "we," "us," or "our"). They govern the HaggleFreight website, mobile applications, and related marketplace services (together, the "Platform").

"Customer" means a person or business seeking transportation. "Transportation Provider" means an independent driver, owner-operator, carrier, or transportation company offering transportation. "You" includes an organization you are authorized to represent. "User" includes either account type.

1. Acceptance and Electronic Agreement

Read these Terms and the agreements presented for your account before accepting. By selecting an unchecked acceptance box and clicking an expressly labeled agreement or registration button, or otherwise expressly accepting these Terms, you agree to them. If you act for an organization, you confirm that you have authority to bind it.

Do not create an account, post shipments, submit bids, or send marketplace messages unless you accept the applicable agreements. Merely visiting a page or receiving a message is not acceptance of an undisclosed contract or purchase.

Your intentional electronic acceptance may be recorded with your account identifier, the document version, the acceptance time, and relevant acceptance records, consistent with the Privacy Policy. Keep a copy of the agreements you accept. We will make the current Terms available through the Platform and provide assistance obtaining the version applicable to your account.

Acceptance governs your use of the Platform. It is not consent to an undisclosed charge, optional marketing, unrelated data use, or a waiver of rights that cannot legally be waived.

2. Marketplace Role and Independent Decisions

HaggleFreight provides software through which Customers and Transportation Providers can connect, post and review shipments, exchange bids, negotiate, communicate, and maintain transaction records. Optional features apply only when actually available and used; mentioning a feature here does not promise its availability.

GoCourier Inc. does not itself drive the vehicles, take physical custody of cargo, or perform the transportation offered by Transportation Providers. Users make their own decisions about counterparties, prices, and shipment acceptance. A notification or transaction record communicates the relevant users' actions; it is not a guarantee of transportation performance.

These Terms do not appoint GoCourier Inc. to accept shipments or sign transportation contracts in your name. They do not establish an employment relationship or authorize a user to bind GoCourier Inc. without our separate agreement.

The legal responsibilities of each party depend on applicable law and actual activities, not merely contractual labels. Nothing here waives a responsibility imposed on GoCourier Inc. or a user, or authorizes an activity requiring a license, registration, permit, or operating authority before the applicable requirements are satisfied.

3. Related Policies and Account Agreements

The Customer Agreement applies to Customer activities, and the Carrier Agreement applies to Transportation Provider activities, when presented and accepted. The Refund Policy and Cancellation Policy apply to the relevant services and transactions as disclosed before acceptance. Links to these documents appear below.

The Privacy Policy explains personal-information handling. Acknowledging that policy does not provide blanket consent for optional collection, tracking, advertising, or other activities requiring separate permission.

Role-specific and transaction-specific terms must be clearly presented. A footer link alone does not authorize a new charge or retroactively change a confirmed transaction. Conflicts between documents are addressed in Section 27.

4. Eligibility and Authorized Use

You must be at least 18 years old, legally able to enter contracts, and authorized to act for any business you represent. A business must ensure that personnel using its account have appropriate authority and understand the applicable agreements.

Use the Platform only for supported services and locations where your activity is lawful. Do not use it to avoid sanctions, licensing restrictions, safety requirements, or other legal obligations. Account approval does not grant transportation authority or permission to perform regulated work.

5. Account Information, Access, and Security

Provide accurate, current account and business information and update material changes, including changes affecting insurance, authority, or the business performing transportation. Do not impersonate another person, falsify verification information, or use credentials belonging to an unrelated carrier.

Protect passwords and authentication methods. Grant account access only to authorized personnel, revoke access when authorization ends, and promptly notify support@hagglefreight.com of suspected unauthorized use. You are responsible for activity you authorize; this does not transfer responsibility for our own security failures to you.

Do not transfer an account without permission, create replacement accounts to evade restrictions, or access information outside your authorization. We may request information reasonably necessary for eligibility, security, fraud prevention, or legal compliance, subject to appropriate privacy disclosures and permissions.

6. Customer and Shipper Responsibilities

Customers must have authority to offer the shipment and provide accurate cargo descriptions, quantities, dimensions, weight, pickup and delivery locations, contacts, timing, equipment requirements, access limitations, and special handling instructions.

Disclose dangerous or regulated characteristics, temperature requirements, loading and unloading arrangements, and any condition materially affecting the service or price before a Transportation Provider accepts. Do not conceal contents or place unnecessary personal information in public listings.

Customers are responsible for lawful cargo, appropriate packaging, access to shipment locations, cargo availability, and paying agreed transportation charges. Review the proposed provider, insurance information, and service terms rather than relying solely on a profile or rating.

A listing does not replace a required bill of lading, permit, shipping document, or legally required disclosure. Customers must promptly communicate material changes and may not impose new requirements without the affected parties' agreement.

7. Transportation Provider Responsibilities

Transportation Providers must determine whether they can legally and safely perform each shipment. They are responsible for required operating authority, licenses, insurance, permits, equipment, vehicle registrations, personnel qualifications, and compliance with applicable transportation laws.

Accurately identify the business performing the transportation. A driver acting for a carrier must have that carrier's authorization. Do not use another business's authority or insurance without lawful authorization or represent that a document covers a shipment when it does not.

Transportation Providers control their vehicles, personnel, driving decisions, and lawful performance of accepted services. They are responsible for applicable safety, cargo-securement, inspection, and hours-of-service obligations. Report material delays, damage, or inability to perform promptly.

Do not transfer, subcontract, or re-broker a shipment contrary to its agreed terms or applicable law. Obtain necessary customer agreement and legally required authority before changing the performing business. These Terms do not authorize unlicensed brokerage.

Do not interact with the Platform while driving when unsafe or unlawful. Maps and suggestions do not replace road restrictions or safe judgment. Each party remains responsible for taxes and reporting duties imposed on it by law; our own obligations are not transferred to users.

8. Listings, Bids, Acceptance, and Changes

A listing, estimate, or unaccepted bid is not a confirmed transportation agreement. Customers and Transportation Providers must mutually accept the price and material shipment terms and satisfy any clearly disclosed booking conditions before treating a shipment as confirmed.

Review the final shipment details, payment conditions, and confirmation before beginning transportation. An automated acknowledgment that information was received is not necessarily acceptance by the other party. The acceptance process must clearly identify the action that confirms the agreement.

Material changes to cargo, timing, services, or charges require the affected parties' agreement. Do not impose undisclosed extra charges or make commitments you lack authority or ability to perform. Platform records are evidence of user activity, not a substitute for legally required documents.

Pricing suggestions, automated estimates, search results, distances, and arrival times are informational and may be inaccurate. They do not guarantee the lowest price, a suitable provider, safe routing, or a particular outcome. Users remain responsible for their final decisions.

9. Free Access and Future Platform Fees

HaggleFreight may offer all or part of the Platform without charge. Unless a specific offer expressly promises otherwise, no fixed period of free access or permanently free service is guaranteed.

We may introduce subscriptions, paid features, or other lawful Platform charges, or change available plans, subject to existing commitments and applicable requirements. These Terms do not set a subscription price or a date when paid access will begin.

Before a new or increased charge applies, we will disclose the price or calculation method, what it covers, when it applies, and the billing arrangements. Affected existing users will receive advance notice and an opportunity to accept or decline where required. We will obtain the applicable agreement and payment authorization before charging.

Creating an account, accepting these Terms, or using free features does not authorize an undisclosed fee, automatic conversion to paid access, or recurring billing. We will not retroactively charge for access or transactions previously offered without a Platform fee. Any specific promotional commitment already made to you remains subject to its disclosed terms.

You may decline a paid offer and stop using affected paid features. Declining does not create a debt for prior free use or prevent account deletion or privacy requests. Free Platform access does not make transportation free: transportation charges remain separate and must be agreed between the relevant users.

Any change to our compensation structure or services remains subject to applicable regulatory, licensing, payment-provider, and app-store requirements. Pricing language in these Terms does not itself authorize a regulated activity.

10. Subscriptions and Trials, When Offered

A subscription offer will disclose its price, billing period, included features and limits, applicable taxes, start date, renewal terms, cancellation process, and refund terms before purchase. Any trial offer will explain its duration and whether it ends, continues without charge, or converts to paid access.

Recurring billing or a trial-to-paid conversion requires your express agreement to the specific offer. General acceptance of these Terms is not that agreement. We will provide confirmations, notices, reminders, and additional consent opportunities required by applicable law and the relevant purchase channel.

You may cancel future renewals through the disclosed billing channel. For subscriptions billed directly by GoCourier Inc., we will provide a straightforward online cancellation method. App-store subscriptions are managed through the relevant store's subscription controls. Cancellation, remaining access, and refunds follow the accepted offer and applicable mandatory rights.

Deleting the app does not cancel a subscription. Changes to existing subscriptions will be disclosed in advance and require renewed authorization where applicable. We will not use silence as agreement to a newly introduced paid plan or make cancellation unnecessarily difficult.

11. Transportation Payments, When Enabled

Transportation charges arise from the Customer's agreement with the Transportation Provider. When an integrated payment feature is available, its disclosures will identify the payment provider and explain authorization, processing charges, refund procedures, and payout conditions before use.

Payments may be processed through Stripe or another identified provider. Transportation Providers using Stripe Connect must complete its required onboarding and accept the applicable Stripe Connected Account Agreement and Stripe Services Agreement. The Stripe Connected Account Agreement is linked below. Necessary payment instructions and information may be shared to process authorized transactions, consistent with the Privacy Policy and required permissions.

Review the total, recipient, and applicable charges before paying. A saved payment method is not permission for new or undisclosed charges. Additional charges, deductions, reversals, or adjustments require a lawful basis and applicable authorization. These Terms do not grant an unlimited right to debit accounts, impose reserves, or retain funds.

Payouts may depend on successful payment, required verification, shipment-completion conditions disclosed before acceptance, and lawful payment-provider reviews. Bank processing and payment disputes may affect timing. A pending balance or estimated payout is not confirmation of a bank deposit or a guarantee of payment.

The payment feature is not represented as a bank account, insured deposit, or escrow service. Using a third-party processor does not remove obligations imposed on GoCourier Inc. Report missing, duplicate, incorrect, or unauthorized payments promptly. We remain responsible for accounting for funds, required refunds, and other obligations imposed on us by law.

12. Cancellations, Refunds, and Transportation Claims

Review the applicable Cancellation Policy, Refund Policy, and transaction terms before confirming a shipment or payment. A cancellation or other charge must be lawful, disclosed, and accepted before it applies. Identify whether a charge is owed to a Transportation Provider, GoCourier Inc., or another provider.

Use the available cancellation process and promptly notify the other party when you cannot proceed. Refunds depend on the service provided, accepted terms, payment channel, and applicable law. These Terms do not remove nonwaivable refund, payment-dispute, or consumer rights.

Report cargo loss, damage, delay, and payment errors promptly and preserve relevant records. HaggleFreight may assist with communication and records but does not guarantee reimbursement, insurance coverage, or resolution of every dispute. This does not excuse our own legal obligations.

Contacting support does not extend a statutory, insurance, payment-provider, or carrier claim deadline. No short reporting period mentioned in a support process overrides a longer nonwaivable legal deadline. Do not falsify claims, conceal payments, or seek duplicate recovery for the same loss.

13. Prohibited and Restricted Shipments

Illegal goods, stolen property, counterfeit goods, and shipments prohibited by applicable law are not permitted. Never conceal cargo contents, evade inspections, or misrepresent a shipment's regulatory status.

Unless a published HaggleFreight policy expressly supports the category and its requirements are satisfied before posting, the Platform does not permit weapons or ammunition, explosives, hazardous materials, regulated chemicals, controlled substances, live animals, human remains, cash, or negotiable instruments. No Platform policy can authorize an unlawful shipment.

Lawful shipments requiring special equipment, temperature control, permits, or handling must be fully disclosed and accepted by a qualified Transportation Provider. The ability to enter an item in a listing does not mean its transportation is permitted or approved by HaggleFreight.

14. User Content and Permission to Operate the Platform

"User Content" includes listings, descriptions, bids, messages, photographs, documents, reviews, and other materials you submit. You retain any ownership rights you have in that content.

You represent that you own the content or have the rights and permissions needed to submit it and authorize its intended use. Do not infringe intellectual-property, confidentiality, privacy, or other rights. Obtain appropriate authority before sharing another person's information.

You grant GoCourier Inc. a nonexclusive, worldwide, royalty-free license to host, store, reproduce, technically process and format, transmit, and display User Content only as reasonably necessary to operate, maintain, secure, and support the Platform and the marketplace activities you use.

This permission includes sharing with the intended audience, relevant transaction participants, and service providers acting on our behalf for those purposes. It does not transfer ownership, authorize unrelated advertising uses, permit sale of your content, or make private messages and documents public. Additional uses requiring permission require separate authorization.

The license ends when content is deleted from our systems, except limited processing of records we are legally required to retain. It does not override the Privacy Policy or your rights under Section 17.

15. Acceptable Use, Reports, and Moderation

Do not submit or promote threats, harassment, discriminatory abuse, hate speech, sexually explicit material, child exploitation or abuse, trafficking, fraud, spam, malicious software, false documents, or infringement of another person's rights. Do not publish private information to intimidate someone or facilitate unlawful conduct.

Do not manipulate bids or reviews, create fake transactions, impersonate users, misrepresent cargo, access accounts without permission, scrape private data, bypass security, or interfere with the Platform. Restrictions on reverse engineering apply only to the extent permitted by law.

Report objectionable content, abuse, suspected fraud, or rights violations through in-app reporting tools or support@hagglefreight.com. Identify the relevant listing, message, or account and explain the concern without unnecessarily disclosing sensitive information. The Platform is not an emergency service; contact emergency services for immediate danger.

We review reports and may remove or restrict content, limit communications, suspend accounts, or terminate repeat or serious offenders. We may investigate using information lawfully available to us and respond to lawful requests from authorities. Content restrictions and moderation do not guarantee that all users or submissions have been screened or that all harm will be prevented.

For intellectual-property complaints, identify the protected work or right, the material at issue, your contact information, and your authority to complain. Do not submit knowingly false reports. Honest reviews, lawful complaints, and communications with regulators are not prohibited merely because they criticize us or another user.

16. Privacy, Permissions, and Other Users' Information

The Privacy Policy explains personal-information collection, use, disclosure, retention, and available rights. These Terms do not replace required disclosures, device permissions, or separate consent.

Where location, tracking, photographs, signatures, or document features are available and used, handling must match the relevant disclosures and permissions. Do not upload unnecessary sensitive information. Accepting the Terms is not blanket authorization for optional analytics, marketing, tracking, or unrelated uses.

Use information received about another user, recipient, location, or shipment only for the relevant transaction, lawful recordkeeping, or another authorized purpose. Protect that information, do not sell it, and do not use it for unsolicited marketing. Separate businesses remain responsible for their own lawful handling of information they receive.

17. Account Deletion and Data Retention

You may initiate account deletion from account settings by selecting "Request Account Deletion" and following the confirmation steps. This applies to Customer and Transportation Provider accounts. For help accessing the process, contact support@hagglefreight.com.

You may also request deletion without installing or reinstalling the app by emailing support@hagglefreight.com with the subject "HaggleFreight Account Deletion." Identify the account email and account type. Never send your password or a complete payment-card number. We may request proportionate verification to prevent unauthorized deletion.

Deletion may be processed manually. We will explain the expected processing time and confirm completion, within applicable legal requirements. Account deletion means deleting the account and associated personal information and User Content, not merely suspending access.

We will delete associated information from systems we control and instruct service providers acting on our behalf as appropriate. Specific records will be retained only when legally required. We will explain the relevant categories and reasons, restrict use to the necessary purpose, and limit retention to the required period. A general business preference is not a reason to keep an entire account indefinitely.

Request copies of records you need before deletion. Other transaction participants may separately hold records they lawfully received and are responsible for their own handling. That does not authorize us to retain otherwise-deletable content merely because it was shared.

Deletion does not erase a lawful payment obligation or prevent a refund or payout that remains due. Those matters do not remove your ability to request deletion. Required retained records must remain limited as described above.

Deleting an account may not cancel an app-store subscription. We will provide relevant cancellation information; use the store's subscription controls to stop renewal. For a subscription billed directly by us, we will stop future renewals as part of processing deletion. You may request deletion without waiting for a subscription period to expire.

18. Verification, Ratings, and Insurance

A profile, document upload, rating, or verification indicator is not a guarantee of identity, current authority, coverage, reliability, or suitability for every shipment. Review what a particular check actually covers and independently confirm information important to your transaction. Our descriptions of checks must accurately reflect the work performed.

Transportation Providers must maintain insurance required by law and their agreements. Customers should confirm cargo coverage, exclusions, and limits before booking. GoCourier Inc. does not itself provide cargo, vehicle, or liability insurance, and using the Platform does not automatically insure a shipment.

Any separately offered coverage must be confirmed by an identified insurer and its actual policy. References to insurance are not a substitute for coverage. These provisions do not transfer our own legal responsibilities to users.

19. Platform Software and Third-Party Services

Subject to these Terms, you receive limited, nonexclusive, nontransferable permission to use the Platform for its intended lawful purposes. You do not acquire ownership of our software, branding, or other intellectual property. Third-party materials remain subject to their applicable licenses.

Maps, payment services, and other third-party services may have separate terms and privacy notices presented for their use. These Terms do not grant permissions that must come from their providers or eliminate obligations we independently owe you.

For an iOS app distributed under Apple's Standard End User License Agreement, that agreement governs the app-software license. These Terms govern marketplace services and do not replace mandatory app-store terms. Apple and Google are not parties to transportation agreements between users.

20. Electronic Communications and Records

We may send necessary account, security, transaction, and service communications through the Platform or your provided contact information. Keep that information current and maintain access to records important to your transactions. Optional promotional messages remain subject to your choices and required consent.

Where the law requires a particular electronic-delivery consent or notice format, we will obtain that consent or provide the required format separately. These Terms do not waive formal-service requirements for legal proceedings or deem a notice received when the law provides otherwise.

21. Restrictions, Suspension, and Service Changes

We may restrict content or access when reasonably necessary to address material breaches, suspected fraud, security or safety risks, unlawful activity, inaccurate verification information, or legal requirements. Where appropriate and lawful, we will explain the restriction and provide a way to contact support about it.

We may update, limit, or discontinue features, subject to paid-service commitments and applicable law. We will provide reasonable advance notice of material changes when practicable; urgent legal, security, or safety circumstances may require immediate action.

Suspension or closure does not authorize confiscation of funds or undisclosed charges. Refunds for undelivered paid services, pending payouts, and access to legally required records remain governed by applicable commitments and law. You may stop using the Platform and request deletion under Section 17.

22. Service Disclaimers

To the extent permitted by law, the Platform is provided on an "as is" and "as available" basis. We do not guarantee uninterrupted or error-free operation, a minimum number of shipments or bids, specific earnings, a particular provider, or a particular business result.

We do not guarantee another user's statements, payment ability, shipment performance, arrival time, or absence of loss. Estimates and automated information may be inaccurate. These provisions do not override an express commitment we make or exclude a warranty, duty, security obligation, or other protection that cannot lawfully be excluded.

23. Limitations of Liability

To the maximum extent permitted by applicable law, GoCourier Inc. and its officers, employees, and service providers are not liable for indirect, incidental, special, consequential, exemplary, or punitive damages arising from Platform use, including indirect lost profits, lost business opportunities, or loss of goodwill.

Responsibility for cargo loss, damage, delay, and transportation performance depends on applicable law and valid agreements. Use of the Platform does not eliminate a claim against a party that is legally responsible.

Nothing here excludes or limits liability for our fraud, willful misconduct, gross negligence, or any liability that cannot lawfully be excluded or limited. These provisions do not erase obligations to account for funds, provide required refunds, or pay money lawfully owed. Limitations apply only to the extent permitted in the relevant jurisdiction.

24. Responsibility for Third-Party Claims

To the extent permitted by law, you agree to indemnify GoCourier Inc. and its officers and employees against third-party claims, reasonable losses, and reasonable legal costs to the extent caused by your unlawful conduct, negligent or intentional misconduct, material breach of these Terms, or infringement of another person's rights through your content or transportation activities.

This obligation does not apply to the extent a claim results from our breach, negligence, or misconduct. We will provide reasonable notice and an opportunity to participate in the defense. Neither party may settle a covered claim by imposing an admission, payment obligation, or nonmonetary obligation on the other without that party's consent, not to be unreasonably withheld.

25. Changes to These Terms

We may revise these Terms and will update the displayed date and version. Material changes will be communicated in advance through appropriate Platform or account notices, except where urgent legal or security circumstances reasonably require shorter notice.

Changes apply prospectively and do not retroactively alter completed transactions or remove accrued rights. We will request renewed acceptance for material contractual changes before applying them to your continued account-based use. An updated footer or silence alone is not authorization for a newly introduced charge or recurring billing.

You may decline a change, stop using affected services, cancel future renewals as applicable, and request account deletion. We will not require acceptance of new paid terms merely to request deletion, access legally required records, or exercise nonwaivable rights.

26. Governing Law and Disputes

These Terms are governed by Delaware law and applicable United States federal law, without using a choice-of-law provision to defeat mandatory protections. This choice does not deprive you of nonwaivable rights provided by otherwise applicable law.

You may contact support@hagglefreight.com to seek informal resolution. Contacting support is not a mandatory prerequisite to exercising a legal right and does not extend a claim deadline. Disputes may be brought in a court with lawful jurisdiction and proper venue.

These Terms do not require arbitration or waive an eligible small-claims proceeding, the right to contact a regulator, or a remedy that cannot lawfully be waived.

27. Document Priority and General Provisions

Mandatory law takes priority. A separate written agreement signed by you and GoCourier Inc. may control the subject it expressly addresses. Otherwise, a clearly presented and accepted transaction or role-specific term controls its subject matter, subject to the fee-disclosure and consent protections in Sections 9 and 10. The Privacy Policy controls its stated data-handling subject matter without reducing statutory rights.

An agreement between users cannot impose obligations on GoCourier Inc. without our agreement. A linked policy does not impose an undisclosed fee or remove a protection expressly preserved here. Conflicting documents should be brought to support's attention before entering a new transaction.

You may not assign your account or obligations without our consent except where the law permits. We may assign this agreement in connection with a legitimate merger, reorganization, or transfer of the relevant business, subject to applicable law, required notices, privacy commitments, and protection of accrued rights.

If a provision is unenforceable, the remaining provisions continue to apply to the extent permitted by law. A failure to enforce a provision once is not a waiver of future enforcement. Accrued payment duties and provisions that reasonably need to survive closure continue only to the extent consistent with law and the deletion requirements above.

28. Contact

GoCourier Inc., operator of HaggleFreight.

Support, account assistance, content reports, deletion requests, and questions about these Terms: support@hagglefreight.com.
`;

const blocks = TERMS_TEXT.trim().split(/\n\s*\n/);

const policyLinks = [
  ['Privacy', '/privacy'],
  ['Refunds', '/refund-policy'],
  ['Cancellations', '/cancellation-policy'],
  ['Customer Agreement', '/customer-agreement'],
  ['Carrier Agreement', '/carrier-agreement'],
];

const styles = {
  page: {
    boxSizing: 'border-box',
    width: '100%',
    padding: 'clamp(16px, 4vw, 40px)',
    background: '#f7f9fc',
    color: '#172333',
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    textAlign: 'left',
  },
  article: {
    boxSizing: 'border-box',
    maxWidth: '940px',
    margin: '0 auto',
    padding: 'clamp(20px, 5vw, 48px)',
    background: '#ffffff',
    border: '1px solid #dde4ed',
    borderRadius: '16px',
    overflowWrap: 'anywhere',
    textAlign: 'left',
  },
  title: {
    fontSize: 'clamp(28px, 5vw, 42px)',
    lineHeight: 1.2,
    margin: '0 0 16px',
    textAlign: 'left',
  },
  meta: {
    fontSize: '14px',
    lineHeight: 1.7,
    margin: '0 0 20px',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: 1.75,
    margin: '0 0 16px',
    textAlign: 'left',
  },
  heading: {
    fontSize: '22px',
    lineHeight: 1.4,
    margin: '32px 0 14px',
    textAlign: 'left',
    scrollMarginTop: '24px',
  },
  nav: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px 20px',
    margin: '20px 0 28px',
    fontSize: '15px',
  },
  link: {
    color: '#155da3',
    textDecoration: 'underline',
  },
  footer: {
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '1px solid #dde4ed',
  },
};

function renderText(text) {
  return text.split(SUPPORT_EMAIL).map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && (
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          style={styles.link}
        >
          {SUPPORT_EMAIL}
        </a>
      )}
      {part}
    </React.Fragment>
  ));
}

export default function TermsOfService() {
  return (
    <main style={styles.page}>
      <article
        style={styles.article}
        aria-labelledby="terms-title"
      >
        <h1 id="terms-title" style={styles.title}>
          HaggleFreight Terms of Service
        </h1>

        <p style={styles.meta}>
          Last updated:{' '}
          <time dateTime="2026-10-12">
            {LAST_UPDATED}
          </time>
          <br />
          Version: {TERMS_VERSION}
        </p>

        <nav aria-label="Key terms" style={styles.nav}>
          <a href="#section-9" style={styles.link}>
            Platform fees
          </a>

          <a href="#account-deletion" style={styles.link}>
            Account deletion
          </a>

          <a href="#related-policies" style={styles.link}>
            Related policies
          </a>
        </nav>

        {blocks.map((block, index) => {
          const heading = block.match(/^(\d+)\.\s+(.+)$/);

          if (heading) {
            const id =
              heading[1] === '17'
                ? 'account-deletion'
                : `section-${heading[1]}`;

            return (
              <h2
                key={index}
                id={id}
                style={styles.heading}
              >
                {block}
              </h2>
            );
          }

          return (
            <p key={index} style={styles.paragraph}>
              {renderText(block)}
            </p>
          );
        })}

        <footer
          id="related-policies"
          style={styles.footer}
        >
          <h2 style={styles.heading}>
            Related policies and support
          </h2>

          <nav
            aria-label="HaggleFreight policies"
            style={styles.nav}
          >
            {policyLinks.map(([label, path]) => (
              <Link
                key={path}
                to={path}
                style={styles.link}
              >
                {label}
              </Link>
            ))}
          </nav>

          <nav
            aria-label="Support and other agreements"
            style={styles.nav}
          >
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              style={styles.link}
            >
              Contact support
            </a>

            <a
              href="https://stripe.com/legal/connect-account"
              style={styles.link}
            >
              Stripe Connected Account Agreement
            </a>

            <Link to="/" style={styles.link}>
              Back to home
            </Link>
          </nav>
        </footer>
      </article>
    </main>
  );
}